import { type INBPTThresholdFilters } from '@/types/eos/filters'
import type SiteDowntimeType from '@/types/eos/SiteDowntimeType'
import type { ISiteMarketData } from '@/types/eos/SiteMarketData'
import type { EOSTableRow, ISiteCurrentEnergyConsumption, ISiteDABid } from '@/types/eos/Table'

import type { IHourlyDABidCreationForm, IHourlyDABidForm, ISiteDABidForm } from '@/types/eos/forms'
import axios, { HttpStatusCode } from 'axios'
import { getNBPTThresholds } from './alerts/nbpt-alerts.service'

export const getChartsMarketData = async (
  iso: string,
  node: string,
  start_datetime: string,
  end_datetime: string
): Promise<ISiteMarketData> => {
  const params = {
    iso,
    node,
    start_datetime,
    end_datetime,
  }
  const result = await axios.get(`${import.meta.env.VITE_APP_EOSS_API}/charts/market-data`, { params })
  return result.data
}

/**
 * Gets all available downtime types.
 *
 * @returns an object containing all available downtime types with their category.
 */
export const getSiteDowntimeTypes = async (): Promise<SiteDowntimeType[]> => {
  const res = await axios.get(import.meta.env.VITE_APP_DOWNTIME_TYPES_ENDPOINT)
  return res.data
}

/**
 * Retrieves the site DA bid for a given site.
 *
 * @param siteId a site identifier, e.g. "GCMNUS"
 * @returns site DA bid data for the given site ID.
 */
export async function getSiteDABid(site_id: string) {
  try {
    const { data } = await axios.get<ISiteDABid>(import.meta.env.VITE_APP_SITE_DA_BID_ENDPOINT, {
      params: {
        site_id,
      },
    })
    return data
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response?.status === HttpStatusCode.NotFound) {
      return null
    } else {
      throw err
    }
  }
}

/**
 * Updates the site DA bid for a given site.
 *
 * @param params "update site DA bid" form field values: site ID, price (USD), and load (MW).
 * @returns the updated site DA bid.
 */
export async function updateSiteDABid(form: ISiteDABidForm) {
  const { data } = await axios.post<ISiteDABid>(import.meta.env.VITE_APP_SITE_DA_BID_ENDPOINT, form)
  return data
}

/**
 * Bulk updates hourly DA bids for a given site and array of hourly DA bids.
 * @returns an array of hourly DA bid objects with the newly-updated values for price and load.
 */
export async function bulkUpdateHourlyDABids(form: IHourlyDABidCreationForm) {
  const res = await axios.post(import.meta.env.VITE_APP_HOURLY_DA_BIDS_ENDPOINT, form)
  return res.data
}

/**
 * Creates a single hourly DA bid.
 * Todo: refactor me! Will also require a back-end refactor.
 *
 * @param params site ID, price (USD), load (MW), and the hour for which to create the DA bid.
 * @returns the created hourly DA bid.
 */
export async function createHourlyDABid(form: IHourlyDABidForm) {
  const creationForm: IHourlyDABidCreationForm = {
    site_id: form.site_id,
    hourly_da_bids: [form],
  }

  const res = await axios.post(import.meta.env.VITE_APP_HOURLY_DA_BIDS_ENDPOINT, creationForm)

  return res.data[0]
}

/**
 * Updates the site DA bid for a given hour.
 *
 * @param params site ID, price (USD), load (MW), and the hour for which to update.
 * @returns an object containing updated site DA bid values.
 */
export async function updateHourlyDABid(form: IHourlyDABidForm) {
  const res = await axios.put(import.meta.env.VITE_APP_HOURLY_DA_BIDS_ENDPOINT, form)
  return res.data
}

/**
 * Gets current site energy consumption.
 *
 * @param siteId a valid site ID.
 * @returns {ISiteCurrentEnergyConsumption} current site energy consumption.
 * @throws {AxiosError}
 */
export async function getCurrentSiteEnergyConsumption(siteId: string) {
  const res = await axios.get<ISiteCurrentEnergyConsumption>(
    import.meta.env.VITE_APP_EOS_SITE_CURRENT_ENERGY_CONSUMPTION,
    {
      params: { site_id: siteId },
    }
  )
  return res.data
}

/**
 * Get 24 hours of EOS Table data for a site starting from a given hour.
 *
 * @param {string} site a site ID.
 * @param {string} start_hour an ISO-formatted date string.
 * @returns {Promise<Array<EOSTableRow>>} EOS Table data for the given query.
 */
export async function getTableData(
  site: string,
  start_hour?: string,
  force_refresh: boolean = false
): Promise<Array<EOSTableRow>> {
  const res = await axios.get<EOSTableRow[]>(import.meta.env.VITE_APP_DASHBOARD_ENDPOINT, {
    params: { site, start_hour, force_refresh },
  })
  return res.data
}

/**
 * Gets the NBPT for a given month and year.
 *
 * @param {number} month
 * @param {number} year
 * @returns the NBPT for the given month and year.
 */
export async function getTableNBPT(month: number, year: number) {
  const filters: INBPTThresholdFilters = {
    page: 1,
    per_page: 10,
    month,
    year,
  }
  const res = await getNBPTThresholds(filters)

  if (res.data[0]) {
    return res.data[0].price_usd
  }

  return null
}
